<template>
  <div ref="rootRef" class="relative max-w-64 min-w-0">
    <button
      type="button"
      class="flex h-[30px] max-w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md px-1.5 text-[13px] font-semibold text-heading hover:bg-tint"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      :title="scopeGroups ? 'Settings pages' : 'Recent pages'"
      @click="isOpen = !isOpen">
      <span class="truncate">{{ label }}</span>
      <ChevronDownIcon class="size-3 shrink-0 text-muted" aria-hidden="true" />
    </button>

    <div
      v-if="isOpen"
      role="menu"
      class="absolute top-full left-0 z-50 mt-1 w-72 rounded-lg bg-white py-1 shadow-popover ring-1 ring-line dark:bg-panel">
      <template v-if="scopeGroups">
        <template v-for="group in scopeGroups" :key="group.label">
          <div class="truncate px-3 pt-1.5 pb-1 text-[11.5px] font-semibold text-muted">{{ group.label }}</div>
          <router-link
            v-for="item in group.items"
            :key="item.to.name"
            role="menuitem"
            :to="{ ...item.to, params: { teamPid: route.params.teamPid } }"
            class="mx-1 flex h-8 cursor-pointer items-center gap-2.5 rounded-md px-2 text-[13px] hover:bg-tint"
            :class="isCurrentItem(item) ? 'font-semibold text-primary' : 'text-heading'"
            :aria-current="isCurrentItem(item) ? 'page' : undefined"
            @click="isOpen = false">
            <component :is="item.icon" class="size-4 shrink-0 text-muted" aria-hidden="true" />
            <span class="min-w-0 flex-1 truncate">{{ item.name }}</span>
            <CheckIcon v-if="isCurrentItem(item)" class="size-4 shrink-0 text-primary" aria-hidden="true" />
          </router-link>
        </template>
      </template>
      <template v-else>
        <div class="px-3 pt-1.5 pb-1 text-[11.5px] font-semibold text-muted">Recent</div>
        <router-link
          v-for="page in recentPages"
          :key="`${page.routeName}:${JSON.stringify(page.params)}`"
          role="menuitem"
          :to="{ name: page.routeName, params: { ...page.params, teamPid: route.params.teamPid } }"
          class="mx-1 flex h-8 items-center gap-3 rounded-md px-2 text-[13px] hover:bg-tint"
          :class="isCurrent(page) ? 'font-semibold text-primary' : 'text-heading'"
          @click="isOpen = false">
          <span class="min-w-0 flex-1 truncate">{{ page.label }}</span>
          <span class="shrink-0 text-xs font-normal text-muted">{{ page.typeLabel }}</span>
        </router-link>
        <div v-if="!recentPages.length" class="px-3 py-2 text-[13px] text-muted">No recent pages yet</div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { shellIcons } from '@dashboard/components/icons/shellIcons'
import type { RecentPage } from '@dashboard/composables/useRecentPages'
import { useRecentPages } from '@dashboard/composables/useRecentPages'
import type { SettingsNavItem } from '@dashboard/data/settingsNavigation'
import { useAccountSettingsNavigation, useProjectSettingsNavigation } from '@dashboard/data/settingsNavigation'
import { useConfigStore } from '@dashboard/stores/configStore'
import { CheckIcon } from '@heroicons/vue/24/outline'
import { onClickOutside, onKeyStroke } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, useTemplateRef } from 'vue'
import { useRoute } from 'vue-router'

const { chevronDown: ChevronDownIcon } = shellIcons

defineProps<{ label: string }>()

const route = useRoute()
const { recentPages, currentPage } = useRecentPages()
const { currentTeam } = storeToRefs(useConfigStore())
const projectNavigation = useProjectSettingsNavigation()
const accountNavigation = useAccountSettingsNavigation()

// Inside settings the menu lists every page of the scope you are in.
const scopeGroups = computed<{ label: string; items: SettingsNavItem[] }[] | null>(() => {
  if (route.meta.settingsScope === 'project') {
    return [{ label: currentTeam.value?.name?.trim() || 'This project', items: projectNavigation.value }]
  }
  if (route.meta.settingsScope === 'account') {
    const { account, agency } = accountNavigation.value
    return agency.length > 0
      ? [
          { label: 'Your account', items: account },
          { label: 'Agency', items: agency }
        ]
      : [{ label: 'Your account', items: account }]
  }
  return null
})

const isOpen = ref(false)
onClickOutside(useTemplateRef<HTMLElement>('rootRef'), () => {
  isOpen.value = false
})
onKeyStroke('Escape', () => {
  isOpen.value = false
})

const isCurrent = (page: RecentPage) =>
  currentPage.value?.routeName === page.routeName &&
  JSON.stringify(currentPage.value.params) === JSON.stringify(page.params)

const isCurrentItem = (item: SettingsNavItem) =>
  item.to.name === route.name || (typeof route.name === 'string' && !!item.childRoutes?.includes(route.name))
</script>
